import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { App, Badge } from "antd";
import { useAuth } from "../../hooks/useAuth";
import ThemeToggle from "../common/ThemeToggle";
import apiService from "../../services/api";
import {
  User,
  LogOut,
  Bell,
  Search,
  Menu,
} from "lucide-react";
import "./Header.css";

const Header = () => {
  const { modal } = App.useApp();
  const { isAuthenticated, user, logout } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);
  const [alertCount, setAlertCount] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchStats = async () => {
      try {
        const response = await apiService.alerts.getAlertStats();
        if (response.data.success) {
          setAlertCount(response.data.data.firing || 0);
        }
      } catch (error) {
        console.error("Failed to fetch alert stats:", error);
      }
    };

    fetchStats();
    const interval = setInterval(fetchStats, 60000); // 1 minute
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const handleLogout = async () => {
    // 防止重复点击
    if (isLoggingOut) {
      return;
    }

    modal.confirm({
      title: "退出登录",
      content: "确定要退出登录吗？",
      okText: "确定",
      cancelText: "取消",
      okType: "danger",
      onOk: async () => {
        setIsLoggingOut(true);
        try {
          await logout();
        } finally {
          setIsLoggingOut(false);
        }
      },
    });
  };

  const handleToggleMobileMenu = () => {
    const sidebar = document.querySelector(".sidebar");
    if (sidebar) {
      sidebar.classList.toggle("mobile-open");
    }
  };

  return (
    <header className="header">
      <div className="header-container">
        {/* 左侧：移动端菜单按钮 + 搜索 */}
        <div className="header-left">
          <button
            className="mobile-menu-btn"
            onClick={handleToggleMobileMenu}
            aria-label="打开菜单"
          >
            <Menu size={24} />
          </button>
          <div className="search-box">
            <Search size={18} className="search-icon" />
            <input type="text" className="search-input" placeholder="搜索..." />
          </div>
        </div>

        {/* 右侧：操作区 + 用户信息 */}
        <div className="header-right">
          {isAuthenticated ? (
            <>
              {/* 工具栏 */}
              <div className="toolbar">
                {/* 主题切换 */}
                <div className="toolbar-item">
                  <ThemeToggle />
                </div>

                {/* 分隔线 */}
                <div className="toolbar-divider" />

                {/* 通知按钮 */}
                <Link to="/alerts" className="toolbar-btn notification-trigger" aria-label="通知">
                  <Badge count={alertCount} size="small" overflowCount={99}>
                    <Bell size={18} />
                  </Badge>
                </Link>
              </div>

              {/* 用户卡片 */}
              <div className="user-dropdown">
                <button className="user-card">
                  {/* 在线状态 */}
                  <div className="status-wrapper">
                    <div className="user-avatar">
                      <User size={16} />
                    </div>
                    <span className="status-online" title="在线" />
                  </div>

                  {/* 用户信息 */}
                  <div className="user-meta">
                    <span className="user-name">{user?.username}</span>
                    <span className="user-badge">
                      <span className="badge-dot" />
                      {user?.role === "admin" ? "管理员" : "用户"}
                    </span>
                  </div>

                  {/* 展开指示 */}
                  <div className="expand-icon">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </button>

                {/* 下拉菜单 */}
                <div className="dropdown-menu">
                  <div className="dropdown-header">
                    <div className="header-avatar">
                      <User size={20} />
                    </div>
                    <div className="header-info">
                      <span className="header-name">{user?.username}</span>
                      <span className="header-email">{user?.email || 'admin@system.local'}</span>
                    </div>
                  </div>
                  <div className="dropdown-divider" />
                  <Link to="/profile" className="dropdown-item">
                    <div className="item-icon-wrapper">
                      <User size={14} />
                    </div>
                    <span>个人中心</span>
                  </Link>
                  <div className="dropdown-divider" />
                  <button
                    className="dropdown-item danger"
                    onClick={handleLogout}
                    disabled={isLoggingOut}
                  >
                    <div className="item-icon-wrapper danger">
                      <LogOut size={14} />
                    </div>
                    <span>{isLoggingOut ? "退出中..." : "退出登录"}</span>
                  </button>
                </div>
              </div>
            </>
          ) : (
            <Link to="/login" className="login-link">
              登录
            </Link>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
